'use client';
import { useState } from 'react';
import { tabbedChartVariants } from './TabbedChart.styles';
import { Container } from '@/components/primitives/Container/Container';
import { toPascalCase } from '@/utils/string-utils';
import { IBaseComponentProps, WithMetadata } from '@/lib/types';
import { IFile, ITabbedChartModularBlock } from '@/.generated';
import { getCSLPAttributes } from '@/utils/type-guards';
import { TabbedBarChart } from '@/helpers/Charts/TabbedBarChart/TabbedBarChart';
import ImageWrapper from '@/helpers/Wrappers/ImageWrapper/ImageWrapper';
import { toEnhancedImage } from '@/utils/image-utils';
import ModalWrapper from '@/helpers/Wrappers/ModalWrapper/ModalWrapper';
import { trackAdobeClick } from '@/utils/adobe/click';

type TabbedChartProps = ITabbedChartModularBlock & WithMetadata<IBaseComponentProps>;

const Default = (props: TabbedChartProps) => {
  const [activeTab, setActiveTab] = useState(0);
  const { wrapper, tabList, tab, tabActive, tabInactive, content } = tabbedChartVariants();

  if (props.hide_component) return null;

  // MaxTuple union includes [] whose element type is `never`, causing filter to infer `never` without this cast
  type TabItem = NonNullable<ITabbedChartModularBlock['tabbed_chart_item']>[number];
  const allItems = (props.tabbed_chart_item ?? []) as TabItem[];

  // Filter out tabs that have no renderable content
  const tabs = allItems.filter(
    (tabItem) => (tabItem.chart_details?.chart_items?.length ?? 0) > 0 || !!tabItem.image?.url
  );
  const currentTab = tabs[activeTab];

  if (tabs.length === 0) return null;

  return (
    <Container
      metaId={props._metadata?.uid}
      id={props.inline_navigation_id}
      componentName="TabbedChart"
      containerBleed={props.use_wide_layout}
    >
      <div className={wrapper()}>
        <div role="tablist" className={tabList()}>
          {tabs.map((tabItem, index) => (
            <button
              key={index}
              role="tab"
              aria-selected={index === activeTab}
              className={`${tab()} ${index === activeTab ? tabActive() : tabInactive()}`}
              onClick={() => {
                trackAdobeClick({
                  clickName: tabItem.tab_title ?? `Tab ${index + 1}`,
                  clickType: 'button',
                  clickLocation: 'tabbedChart',
                });
                setActiveTab(index);
              }}
              {...getCSLPAttributes(tabItem.$?.tab_title)}
            >
              {tabItem.tab_title}
            </button>
          ))}
        </div>
        <div className={content()}>
          {currentTab?.tab_type === 'chart' && currentTab.chart_details && (
            <TabbedBarChart key={activeTab} chartDetails={currentTab.chart_details} />
          )}
          {currentTab?.tab_type === 'image' && currentTab.image && (
            <InlineChartImage imageData={{ tabTitle: currentTab.tab_title, ...currentTab.image }} />
          )}
        </div>
      </div>
    </Container>
  );
};

const variants = {
  Default,
};

export const TabbedChart = (props: TabbedChartProps) => {
  const Component = props.component_variant
    ? variants[toPascalCase(props.component_variant) as keyof typeof variants]
    : Default;
  return <Component {...props} />;
};

const InlineChartImage = ({ imageData }: { imageData: IFile & { tabTitle?: string } }) => {
  const [isModalOpened, setIsModalOpened] = useState(false);

  const chartImage = toEnhancedImage(imageData, {
    alternateText: imageData.tabTitle || 'Tabbed Chart Image',
    fallbackWidth: 1200,
    fallbackHeight: 800,
  });

  const handleImageClick = () => {
    trackAdobeClick({
      clickName: imageData.title ?? 'Expand chart image',
      clickType: 'button',
      clickLocation: 'tabbedChart',
    });
    setIsModalOpened(true);
  };

  return (
    <>
      <div
        className="relative w-full"
        tabIndex={0}
        onClick={handleImageClick}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleImageClick()}
      >
        <ImageWrapper image={chartImage} imageClassName="w-full h-auto" />
      </div>
      <ModalWrapper
        size="xl"
        isOpen={isModalOpened}
        onClose={() => setIsModalOpened(false)}
        title=""
      >
        <ImageWrapper image={chartImage} imageClassName="w-full h-auto" />
      </ModalWrapper>
    </>
  );
};
